import IWorkflowElement from "@/interfaces/workflow-element.interfaces";
import IWorkflowEngine from "@/interfaces/workflow-engine.interfaces";
import { IWorkflowStep, StepType } from "@/interfaces/workflow-step.interface";
import { WorkflowEditDialogMessages } from "@/messages/workflow/workflow-edit-dialog.messages";
import { FilterType, IFilter } from "@/types/filter.types";
import { SelectData, TabItem } from "@/types/form.types";
import { WorkflowTypes } from "@/types/workflow.types";

import { WorkflowTabs } from "./types";

export const TABS_ITEMS = (messages: WorkflowEditDialogMessages): TabItem<WorkflowTabs>[] => {
  return [
    { value: "workflowTab", label: messages.tabWorkflowLabel },
    { value: "workflowStepsTab", label: messages.tabStepsDataLabel },
    { value: "workflowSettingsTab", label: messages.tabSettingsLabel },
  ];
};

export const ALLOW_ATTENDANT_TRANSFER_OPTIONS = (
  messages: WorkflowEditDialogMessages
): SelectData<boolean>[] => {
  return [
    { value: true, label: messages.yesOption },
    { value: false, label: messages.noOption },
  ];
};

export const STEP_TYPE_OPTIONS = (messages: WorkflowEditDialogMessages): SelectData<string>[] => {
  return [
    { value: StepType.StepTypeS, label: messages.stepTypeSOption },
    { value: StepType.StepTypeD, label: messages.stepTypeDOption },
  ];
};

const workflowElements = [
  {
    id: 1,
    type: "1 - Begin",
    shape: "1 - Oval",
    rowDate: "2023-11-15T11:54:47",
  },
  {
    id: 2,
    type: "2 - Message",
    shape: "2 - Rectangle",
    rowDate: "2023-11-15T15:05:22",
  },
  {
    id: 3,
    type: "3 - Menu",
    shape: "4 - Square",
    rowDate: "2023-11-15T11:54:47",
  },
  {
    id: 4,
    type: "4 - Process",
    shape: "2 - Rectangle",
    rowDate: "2023-11-15T11:54:47",
  },
  {
    id: 5,
    type: "5 - Question",
    shape: "4 - Square",
    rowDate: "2023-11-15T11:54:47",
  },
  {
    id: 6,
    type: "6 - Input",
    shape: "3 - Diamond",
    rowDate: "2023-11-15T11:54:47",
  },
  {
    id: 7,
    type: "7 - End",
    shape: "1 - Oval",
    rowDate: "2023-11-15T11:54:47",
  },
];

export const WORKFLOW_ELEMENT_ID_OPTIONS = (): SelectData<number>[] => {
  if (!workflowElements) return [];
  return workflowElements.map((element) => ({
    value: element.id,
    label: element.type,
  }));
};

export const WORKFLOW_ENGINE_ID_OPTIONS = (
  workflowEngine: IWorkflowEngine[] | undefined,
  messages: WorkflowEditDialogMessages
): SelectData<number>[] => {
  const noFunctionOption = { value: 0, label: messages.noneOption };

  if (!workflowEngine) return [noFunctionOption];

  return [
    noFunctionOption,
    ...workflowEngine.map((engine) => ({
      value: engine.id,
      label: engine.name,
    })),
  ];
};


export const EXCEPTION_SOURCE_WORKFLOW_STEP_ID_OPTIONS = (
  steps: IWorkflowStep[],
  messages: WorkflowEditDialogMessages
): SelectData<number>[] => {
  const noExceptionStepOption = { value: 0, label: messages.noneOption };

  const options = steps.map((step) => ({
    value: step.id,
    label: step.id.toString(),
  }));

  return [noExceptionStepOption, ...options];
};

export const DESTINATION_WORKFLOW_STEP_ID_OPTIONS = (
  steps: IWorkflowStep[],
  messages: WorkflowEditDialogMessages
): SelectData<number>[] => {
  const noDestinationStepOption = { value: 0, label: messages.noneOption };

  const options = steps.map((step) => ({
    value: step.id,
    label: step.id.toString(),
  }));

  return [noDestinationStepOption, ...options];
};

/** Extrai steps referenciados no JSON de menuOption */
export function getMenuOptionTargetSteps(
  menuOption: string | null | undefined,
  allSteps: IWorkflowStep[]
): IWorkflowStep[] {
  const raw = String(menuOption ?? "").trim();
  if (!raw || allSteps.length === 0) return [];

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];

    const names = new Set<string>();
    parsed.forEach((item: unknown) => {
      if (item && typeof item === "object") {
        Object.values(item as Record<string, unknown>).forEach((value) => {
          if (typeof value === "string" && value.trim()) {
            names.add(value.trim());
          }
        });
      }
    });

    return allSteps
      .filter((step) => names.has(String(step.stepName ?? "").trim()))
      .sort((a, b) => (a.stepNumber ?? 0) - (b.stepNumber ?? 0));
  } catch {
    return [];
  }
}

/** Verifica se o menuOption do step (JSON) referencia o stepName dado (ex.: opção de menu aponta para outra etapa) */
export function menuOptionReferencesStepName(step: IWorkflowStep, stepName: string): boolean {
  const raw = String(step.menuOption ?? "").trim();
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return false;
    const name = String(stepName).trim();
    return parsed.some((item: unknown) => {
      if (item && typeof item === "object" && item !== null) {
        return Object.values(item as Record<string, unknown>).some(
          (v) => typeof v === "string" && v.trim() === name
        );
      }
      return false;
    });
  } catch {
    return false;
  }
}

/** True se este step aponta para algum outro (goto, exceção ou opção de menu com stepName de outro step) */
export function stepPointsToAnotherStep(
  step: IWorkflowStep,
  allSteps: IWorkflowStep[] | undefined
): boolean {
  if (!allSteps || allSteps.length === 0) return false;
  if (String(step.gotoWorkflowStepName ?? "").trim() !== "") return true;
  if (String(step.exceptionSourceWorkflowStepName ?? "").trim() !== "") return true;
  return allSteps.some(
    (s) => s.id !== step.id && menuOptionReferencesStepName(step, String(s.stepName ?? "").trim())
  );
}

/** Retorna os steps para os quais este step aponta (goto, exceção ou menu), ordenados por stepNumber */
export function getStepsPointedToByStep(
  step: IWorkflowStep,
  allSteps: IWorkflowStep[] | undefined
): IWorkflowStep[] {
  if (!allSteps || allSteps.length === 0) return [];
  const targets = new Set<string>();
  const gotoName = String(step.gotoWorkflowStepName ?? "").trim();
  if (gotoName) targets.add(gotoName);
  const exceptionName = String(step.exceptionSourceWorkflowStepName ?? "").trim();
  if (exceptionName) targets.add(exceptionName);
  allSteps.forEach((s) => {
    if (s.id !== step.id && menuOptionReferencesStepName(step, String(s.stepName ?? "").trim())) {
      targets.add(String(s.stepName ?? "").trim());
    }
  });
  return allSteps
    .filter((s) => s.id !== step.id && targets.has(String(s.stepName ?? "").trim()))
    .sort((a, b) => (a.stepNumber ?? 0) - (b.stepNumber ?? 0));
}

export type StepPointedToDetail = {
  step: IWorkflowStep;
  viaGoto: boolean;
  viaException: boolean;
  viaMenuOption: boolean;
};

/** Como getStepsPointedToByStep, mas com indicação de como aponta (goto, exceção, menu) para cada um */
export function getStepsPointedToByStepWithDetail(
  step: IWorkflowStep,
  allSteps: IWorkflowStep[] | undefined
): StepPointedToDetail[] {
  if (!allSteps || allSteps.length === 0) return [];
  const gotoName = String(step.gotoWorkflowStepName ?? "").trim();
  const exceptionName = String(step.exceptionSourceWorkflowStepName ?? "").trim();
  const byName = new Map<
    string,
    { step: IWorkflowStep; viaGoto: boolean; viaException: boolean; viaMenuOption: boolean }
  >();
  allSteps.forEach((s) => {
    if (s.id === step.id) return;
    const name = String(s.stepName ?? "").trim();
    if (!name) return;
    const viaGoto = gotoName !== "" && name === gotoName;
    const viaException = exceptionName !== "" && name === exceptionName;
    const viaMenuOption = menuOptionReferencesStepName(step, name);
    if (viaGoto || viaException || viaMenuOption) {
      const existing = byName.get(name);
      if (existing) {
        existing.viaGoto = existing.viaGoto || viaGoto;
        existing.viaException = existing.viaException || viaException;
        existing.viaMenuOption = existing.viaMenuOption || viaMenuOption;
      } else {
        byName.set(name, { step: s, viaGoto, viaException, viaMenuOption });
      }
    }
  });
  return Array.from(byName.values()).sort(
    (a, b) => (a.step.stepNumber ?? 0) - (b.step.stepNumber ?? 0)
  );
}

/** InUse = true quando: outro step referencia este (goto/exceção/menu) OU este step aponta para outro */
export const isWorkflowStepInUse = (
  step: IWorkflowStep,
  allSteps: IWorkflowStep[] | undefined
): boolean => {
  if (!allSteps || allSteps.length === 0) return false;
  const stepName = String(step.stepName ?? "").trim();
  if (!stepName) return false;
  const othersReferenceThis = allSteps.some(
    (s) =>
      s.id !== step.id &&
      (String(s.gotoWorkflowStepName ?? "").trim() === stepName ||
        String(s.exceptionSourceWorkflowStepName ?? "").trim() === stepName ||
        menuOptionReferencesStepName(s, stepName))
  );
  const thisPointsToOthers = stepPointsToAnotherStep(step, allSteps);
  return othersReferenceThis || thisPointsToOthers;
};

/** Opções por stepName para autocomplete de etapa de exceção */
export const EXCEPTION_SOURCE_WORKFLOW_STEP_NAME_OPTIONS = (
  steps: IWorkflowStep[],
  messages: WorkflowEditDialogMessages
): SelectData<string>[] => {
  const noOption = { value: "", label: messages.noneOption };
  const options = steps.map((step) => ({
    value: step.stepName,
    label: step.stepName,
  }));
  return [noOption, ...options];
};

/** Opções por stepName para autocomplete de próxima etapa (goto) */
export const DESTINATION_WORKFLOW_STEP_NAME_OPTIONS = (
  steps: IWorkflowStep[],
  messages: WorkflowEditDialogMessages
): SelectData<string>[] => {
  const noOption = { value: "", label: messages.noneOption };
  const options = steps.map((step) => ({
    value: step.stepName,
    label: step.stepName,
  }));
  return [noOption, ...options];
};

/** Colunas do grid de etapas (mesma ordem do backoffice) */
export const WORKFLOW_STEP_TABLE_COLUMNS = (messages: WorkflowEditDialogMessages) => {
  return [
    messages.stageColumn,
    messages.stepNameColumn,
    messages.elementColumn,
    messages.stepTypeColumn,
    messages.engineColumn,
    messages.exceptionStepColumn,
    messages.destinationStepColumn,
    messages.inUseColumn,
    messages.chatTransferColumn,
    messages.optionsColumn,
  ];
};

export const initialFilterElementsValues: IFilter<IWorkflowElement>[] = [
  {
    property: "shape",
    value: "",
    filterType: FilterType.Contains,
  },
];

export const initialFilterEngineValues: IFilter<IWorkflowEngine>[] = [
  {
    property: "name",
    value: "",
    filterType: FilterType.Contains,
  },
];

export const WORKFLOW_TYPE_OPTIONS = (
  messages: WorkflowEditDialogMessages
): SelectData<number>[] => [
  { label: messages.startFlow, value: WorkflowTypes.StartFlow },
  { label: messages.orderStatus, value: WorkflowTypes.OrderStatus },
  { label: messages.shopping, value: WorkflowTypes.Shopping },
];

export const pageNumber = 1;
export const pageSize = 200;
